// =============================================================================
// GOUV EMAIL CONFIG — Emails & Notifications centralisés
// 3 onglets : Templates (objet + corps HTML/text + variables), Destinataires
// (par event), Expéditeurs SMTP (lecture seule, édition complète via MailConfig)
// Source : collections email_templates, email_recipients, mail_senders
// =============================================================================
import { useState, useEffect } from "react";
import {
  Mail, FileText, Users, Server, Plus, Edit, Trash2, Save, X,
  Eye, Loader2, RefreshCw, Send, ArrowLeft, AlertCircle, Bell,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_COLORS = {
  onboarding: { bg: "bg-cyan-500/10", text: "text-cyan-400", border: "border-cyan-500/30" },
  notification: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/30" },
  alert: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30" },
  marketing: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/30" },
};

const CATEGORY_LABELS = {
  onboarding: "Onboarding",
  notification: "Notification",
  alert: "Alerte",
  marketing: "Marketing",
};

function getHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const DEFAULT_REMINDER_SETTINGS = {
  jamais_connecte_jours: 7,
  inactivite_jours: 7,
  max_relances: 10,
  skip_si_aucun_dossier_actif: true,
  actif: true,
};

export default function GouvEmailConfig({ currentUser }) {
  const [tab, setTab] = useState("templates"); // templates | recipients | senders | reminders
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [senders, setSenders] = useState([]);
  const [reminderSettings, setReminderSettings] = useState(DEFAULT_REMINDER_SETTINGS);
  const [savingReminders, setSavingReminders] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editingTemplate, setEditingTemplate] = useState(null); // doc or null
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewVars, setPreviewVars] = useState({});
  const [previewResult, setPreviewResult] = useState(null);

  const [editingRecipients, setEditingRecipients] = useState(null);
  const [editingSender, setEditingSender] = useState(null);

  const userRole = currentUser?.role || currentUser?.rôle || "user";
  const isSuperAdmin = currentUser?.is_super_admin || userRole === "super_admin";
  // Admin + super_admin peuvent éditer (sécurité = côté backend qui vérifie le token)
  const canEdit = isSuperAdmin || userRole === "admin";

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Filtre `owner=system` (JJ 2026-05-20) : Gouvernance affiche uniquement
      // les templates Système (alertes Gamora, AG-04, etc.). Les templates
      // Métier (invitation installateur, invitation user) sont gérés depuis
      // Design Studio désormais.
      const [tplsRes, recsRes, sndsRes, remRes] = await Promise.all([
        fetch(`${API}/api/governance/email-config/templates?owner=system`, { headers: getHeaders(), credentials: "include" }),
        fetch(`${API}/api/governance/email-config/recipients`, { headers: getHeaders(), credentials: "include" }),
        fetch(`${API}/api/governance/email-config/senders`, { headers: getHeaders(), credentials: "include" }),
        fetch(`${API}/api/governance/email-config/reminders`, { headers: getHeaders(), credentials: "include" }),
      ]);
      if (tplsRes.ok) setTemplates((await tplsRes.json()).templates || []);
      if (recsRes.ok) setRecipients((await recsRes.json()).recipients || []);
      if (sndsRes.ok) setSenders((await sndsRes.json()).senders || []);
      if (remRes.ok) {
        const r = await remRes.json();
        setReminderSettings({
          jamais_connecte_jours: r.jamais_connecte_jours ?? 7,
          inactivite_jours: r.inactivite_jours ?? 7,
          max_relances: r.max_relances ?? 10,
          skip_si_aucun_dossier_actif: r.skip_si_aucun_dossier_actif ?? true,
          actif: r.actif ?? true,
        });
      }
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  // ─── Templates ─────────────────────────────────────────────────────────

  const startEditTemplate = (tpl) => {
    setEditingTemplate({ ...tpl, _is_new: false });
  };

  const startNewTemplate = () => {
    setEditingTemplate({
      _is_new: true,
      key: "",
      tenant_id: "tef",
      category: "notification",
      name: "",
      description: "",
      subject: "",
      from_name: "",
      from_email: "",
      reply_to: "",
      html_body: "",
      text_body: "",
      variables: [],
      active: true,
    });
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    const t = editingTemplate;
    if (!t.key.trim() || !t.name.trim() || !t.subject.trim()) {
      toast.error("Clé, nom et objet requis");
      return;
    }
    try {
      const url = t._is_new
        ? `${API}/api/governance/email-config/templates`
        : `${API}/api/governance/email-config/templates/${encodeURIComponent(t.key)}?tenant_id=${encodeURIComponent(t.tenant_id || "")}`;
      const method = t._is_new ? "POST" : "PUT";
      const { _is_new, _id, version, created_at, updated_at, updated_by, ...payload } = t;
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(t._is_new ? "Template créé" : "Template mis à jour");
        setEditingTemplate(null);
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de sauvegarde");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Supprimer le template '${tpl.key}' ?`)) return;
    try {
      const res = await fetch(
        `${API}/api/governance/email-config/templates/${encodeURIComponent(tpl.key)}?tenant_id=${encodeURIComponent(tpl.tenant_id || "")}`,
        { method: "DELETE", headers: getHeaders(), credentials: "include" }
      );
      if (res.ok) {
        toast.success("Template supprimé");
        loadAll();
      } else {
        toast.error("Erreur de suppression");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const openPreview = (tpl) => {
    setPreviewTemplate(tpl);
    // Pré-remplir les variables requises avec des valeurs d'exemple
    const initial = {};
    (tpl.variables || []).forEach((v) => {
      initial[v.key] = v.default || `<exemple ${v.key}>`;
    });
    setPreviewVars(initial);
    setPreviewResult(null);
  };

  const runPreview = async () => {
    if (!previewTemplate) return;
    try {
      const url = `${API}/api/governance/email-config/templates/${encodeURIComponent(previewTemplate.key)}/preview?tenant_id=${encodeURIComponent(previewTemplate.tenant_id || "")}`;
      const res = await fetch(url, {
        method: "POST", headers: getHeaders(), credentials: "include",
        body: JSON.stringify({ variables: previewVars }),
      });
      if (res.ok) {
        setPreviewResult(await res.json());
      } else {
        toast.error("Erreur de rendu");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  // ─── Recipients ────────────────────────────────────────────────────────

  const startEditRecipients = (rec) => {
    setEditingRecipients({ ...rec, _is_new: false });
  };

  const startNewRecipients = () => {
    setEditingRecipients({
      _is_new: true,
      event_key: "",
      tenant_id: "tef",
      name: "",
      description: "",
      to: [],
      cc: [],
      bcc: [],
      active: true,
    });
  };

  const saveRecipients = async () => {
    if (!editingRecipients) return;
    const r = editingRecipients;
    if (!r.event_key.trim() || !r.name.trim()) {
      toast.error("Event key et nom requis");
      return;
    }
    try {
      const url = r._is_new
        ? `${API}/api/governance/email-config/recipients`
        : `${API}/api/governance/email-config/recipients/${encodeURIComponent(r.event_key)}?tenant_id=${encodeURIComponent(r.tenant_id || "")}`;
      const method = r._is_new ? "POST" : "PUT";
      const { _is_new, _id, created_at, updated_at, updated_by, ...payload } = r;
      const res = await fetch(url, {
        method, headers: getHeaders(), credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(r._is_new ? "Destinataires créés" : "Destinataires mis à jour");
        setEditingRecipients(null);
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const deleteRecipients = async (rec) => {
    if (!window.confirm(`Supprimer la config '${rec.event_key}' ?`)) return;
    try {
      const res = await fetch(
        `${API}/api/governance/email-config/recipients/${encodeURIComponent(rec.event_key)}?tenant_id=${encodeURIComponent(rec.tenant_id || "")}`,
        { method: "DELETE", headers: getHeaders(), credentials: "include" }
      );
      if (res.ok) {
        toast.success("Destinataires supprimés");
        loadAll();
      } else {
        toast.error("Erreur de suppression");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  // ─── Senders (édition basique) ─────────────────────────────────────────

  const startEditSender = (s) => {
    setEditingSender({
      _original_email: s.email,
      display_name: s.display_name || "",
      email: s.email || "",
      reply_to: s.reply_to || "",
      enabled: s.enabled !== false,
    });
  };

  const saveSender = async () => {
    if (!editingSender) return;
    try {
      const payload = {
        display_name: editingSender.display_name,
        email: editingSender.email,
        reply_to: editingSender.reply_to,
        enabled: editingSender.enabled,
      };
      const res = await fetch(
        `${API}/api/governance/email-config/senders/${encodeURIComponent(editingSender._original_email)}`,
        { method: "PUT", headers: getHeaders(), credentials: "include", body: JSON.stringify(payload) },
      );
      if (res.ok) {
        toast.success("Sender mis à jour");
        setEditingSender(null);
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  // ─── Relances (cron installateurs) ─────────────────────────────────────

  const saveReminderSettings = async () => {
    setSavingReminders(true);
    try {
      const res = await fetch(`${API}/api/governance/email-config/reminders`, {
        method: "PUT",
        headers: getHeaders(),
        credentials: "include",
        body: JSON.stringify(reminderSettings),
      });
      if (res.ok) {
        toast.success("Paramètres de relance enregistrés");
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSavingReminders(false);
    }
  };

  const triggerSeed = async () => {
    if (!window.confirm("Réinitialiser les templates et destinataires par défaut ? (idempotent)")) return;
    try {
      const res = await fetch(`${API}/api/governance/email-config/seed`, {
        method: "POST", headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seed OK : +${data.templates_added} templates, +${data.recipients_added} destinataires`);
        loadAll();
      } else {
        toast.error("Erreur de seed");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-400" />
            Emails & Notifications
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Source unique pour tous les emails OmniHub. Zéro hardcoding dans le code.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} className="border-zinc-700">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Actualiser
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={triggerSeed} className="border-amber-500/30 text-amber-400">
              Réinitialiser seed
            </Button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-zinc-800">
        {[
          { id: "templates", label: "Templates", icon: FileText, count: templates.length },
          { id: "recipients", label: "Destinataires", icon: Users, count: recipients.length },
          { id: "senders", label: "Expéditeurs SMTP", icon: Server, count: senders.length },
          { id: "reminders", label: "Relances", icon: Bell, count: null },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "text-cyan-400 border-cyan-400"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
            data-testid={`email-config-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.count !== null && (
              <Badge variant="outline" className="text-[10px] border-zinc-700">{t.count}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* ═══ TEMPLATES ═══ */}
      {tab === "templates" && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex justify-end">
              <Button size="sm" onClick={startNewTemplate} className="bg-cyan-600 hover:bg-cyan-500">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Nouveau template
              </Button>
            </div>
          )}

          {templates.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aucun template configuré</p>
              {canEdit && (
                <Button onClick={triggerSeed} variant="outline" size="sm" className="mt-3 border-cyan-500/30 text-cyan-400">
                  Lancer le seed initial
                </Button>
              )}
            </div>
          ) : (
            templates.map((tpl) => {
              const cat = CATEGORY_COLORS[tpl.category] || CATEGORY_COLORS.notification;
              return (
                <div
                  key={`${tpl.tenant_id}-${tpl.key}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4"
                  data-testid={`template-${tpl.key}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[10px] ${cat.bg} ${cat.text} ${cat.border} border`}>
                          {CATEGORY_LABELS[tpl.category] || tpl.category}
                        </Badge>
                        <code className="text-xs text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{tpl.key}</code>
                        {tpl.tenant_id && (
                          <span className="text-[10px] text-zinc-600 uppercase">tenant: {tpl.tenant_id}</span>
                        )}
                        {!tpl.active && (
                          <Badge className="text-[10px] bg-zinc-700 text-zinc-400">inactif</Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-white">{tpl.name}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{tpl.description || "—"}</p>
                      <p className="text-xs text-zinc-400 mt-2 truncate">
                        <span className="text-zinc-500">Objet : </span>{tpl.subject}
                      </p>
                      {(tpl.variables || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tpl.variables.map((v) => (
                            <span key={v.key} className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/10 text-purple-400 font-mono">
                              {`{{${v.key}}}`}{v.required ? "*" : ""}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openPreview(tpl)} className="text-zinc-400 hover:text-cyan-400 h-7">
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => startEditTemplate(tpl)} className="text-zinc-400 hover:text-white h-7">
                            <Edit className="w-3.5 h-3.5 mr-1" /> Éditer
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteTemplate(tpl)} className="text-zinc-400 hover:text-red-400 h-7">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ═══ RECIPIENTS ═══ */}
      {tab === "recipients" && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex justify-end">
              <Button size="sm" onClick={startNewRecipients} className="bg-cyan-600 hover:bg-cyan-500">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Nouvelle config destinataires
              </Button>
            </div>
          )}

          {recipients.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aucune config de destinataires</p>
            </div>
          ) : (
            recipients.map((rec) => (
              <div
                key={`${rec.tenant_id}-${rec.event_key}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4"
                data-testid={`recipients-${rec.event_key}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-xs text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{rec.event_key}</code>
                      {rec.tenant_id && <span className="text-[10px] text-zinc-600 uppercase">tenant: {rec.tenant_id}</span>}
                    </div>
                    <p className="text-sm font-semibold text-white">{rec.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{rec.description || "—"}</p>
                    <div className="mt-2 space-y-1 text-xs">
                      {rec.to?.length > 0 && (
                        <p><span className="text-zinc-500 inline-block w-10">To :</span> <span className="text-zinc-200 font-mono">{rec.to.join(", ")}</span></p>
                      )}
                      {rec.cc?.length > 0 && (
                        <p><span className="text-zinc-500 inline-block w-10">Cc :</span> <span className="text-zinc-300 font-mono">{rec.cc.join(", ")}</span></p>
                      )}
                      {rec.bcc?.length > 0 && (
                        <p><span className="text-zinc-500 inline-block w-10">Bcc :</span> <span className="text-zinc-400 font-mono">{rec.bcc.join(", ")}</span></p>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditRecipients(rec)} className="text-zinc-400 hover:text-white h-7">
                        <Edit className="w-3.5 h-3.5 mr-1" /> Éditer
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteRecipients(rec)} className="text-zinc-400 hover:text-red-400 h-7">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══ SENDERS ═══ */}
      {tab === "senders" && (
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              Édition basique ici (nom affiché, email, reply-to, actif). La config SMTP avancée
              (host, port, mot de passe, test connexion) se gère depuis
              <strong className="font-semibold"> Management → Configuration Email</strong>.
            </p>
          </div>

          {senders.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Server className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aucun expéditeur SMTP configuré</p>
            </div>
          ) : (
            senders.map((s, idx) => (
              <div key={idx} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{s.display_name || s.email}</p>
                    <p className="text-xs text-zinc-500 font-mono">{s.email}</p>
                    {s.reply_to && (
                      <p className="text-[11px] text-zinc-600 mt-0.5">Reply-To : <span className="font-mono">{s.reply_to}</span></p>
                    )}
                    {s.smtp?.host && (
                      <p className="text-[11px] text-zinc-600 mt-1">
                        SMTP : {s.smtp.host}:{s.smtp.port || 587}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    {s.enabled !== false ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">actif</Badge>
                    ) : (
                      <Badge className="bg-zinc-700 text-zinc-400 text-xs">inactif</Badge>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => startEditSender(s)} className="text-zinc-400 hover:text-white h-7 mt-1">
                        <Edit className="w-3.5 h-3.5 mr-1" /> Éditer
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══ RELANCES ═══ */}
      {tab === "reminders" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-cyan-400" />
                Cron de relance installateurs
              </h3>
              <p className="text-xs text-zinc-500 mt-1">
                Job quotidien qui envoie un email de relance aux installateurs jamais connectés
                ou inactifs depuis un certain temps. Skip automatique si l'installateur n'a aucun
                dossier actif assigné.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">
                  Jamais connecté — seuil (jours)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={reminderSettings.jamais_connecte_jours}
                  onChange={(e) => setReminderSettings({
                    ...reminderSettings,
                    jamais_connecte_jours: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })}
                  disabled={!canEdit}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="reminder-jamais-connecte-jours"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Relance après ce nombre de jours sans 1ère connexion (défaut 7).
                </p>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs">
                  Inactivité — seuil (jours)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={reminderSettings.inactivite_jours}
                  onChange={(e) => setReminderSettings({
                    ...reminderSettings,
                    inactivite_jours: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })}
                  disabled={!canEdit}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="reminder-inactivite-jours"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Relance après ce nombre de jours sans login récent (défaut 7).
                </p>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs">
                  Nombre max de relances par installateur
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={reminderSettings.max_relances}
                  onChange={(e) => setReminderSettings({
                    ...reminderSettings,
                    max_relances: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })}
                  disabled={!canEdit}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="reminder-max-relances"
                />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Arrête de relancer après ce nombre d'envois (défaut 10).
                </p>
              </div>

              <div className="flex flex-col justify-center gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderSettings.skip_si_aucun_dossier_actif}
                    onChange={(e) => setReminderSettings({
                      ...reminderSettings,
                      skip_si_aucun_dossier_actif: e.target.checked,
                    })}
                    disabled={!canEdit}
                    data-testid="reminder-skip-aucun-dossier"
                  />
                  <span className="text-sm text-zinc-300">
                    Skip si aucun dossier actif assigné
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderSettings.actif}
                    onChange={(e) => setReminderSettings({
                      ...reminderSettings,
                      actif: e.target.checked,
                    })}
                    disabled={!canEdit}
                    data-testid="reminder-actif"
                  />
                  <span className="text-sm text-zinc-300">
                    Cron de relance activé
                  </span>
                </label>
              </div>
            </div>

            {canEdit && (
              <div className="flex justify-end pt-2 border-t border-zinc-800">
                <Button
                  onClick={saveReminderSettings}
                  disabled={savingReminders}
                  className="bg-cyan-600 hover:bg-cyan-500"
                  data-testid="reminder-save"
                >
                  {savingReminders ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Enregistrer
                </Button>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              Ces paramètres alimentent le job APScheduler. Le template d'email utilisé est
              <code className="mx-1 px-1 py-0.5 rounded bg-zinc-800 text-cyan-400">installateur_relance</code>
              (à créer dans l'onglet Templates si absent). Le cron ne tourne pas tant que le job n'a pas
              été enregistré côté backend.
            </p>
          </div>
        </div>
      )}

      {/* ═══ DIALOG : ÉDITEUR TEMPLATE ═══ */}
      <Dialog open={!!editingTemplate} onOpenChange={(o) => !o && setEditingTemplate(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingTemplate?._is_new ? "Nouveau template" : `Modifier : ${editingTemplate?.name}`}
            </DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Clé technique *</Label>
                  <Input
                    value={editingTemplate.key}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, key: e.target.value })}
                    disabled={!editingTemplate._is_new}
                    placeholder="installateur_invitation"
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Tenant</Label>
                  <Input
                    value={editingTemplate.tenant_id || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, tenant_id: e.target.value })}
                    placeholder="tef (vide = global)"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Catégorie</Label>
                  <Select value={editingTemplate.category} onValueChange={(v) => setEditingTemplate({ ...editingTemplate, category: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Nom affiché</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Description</Label>
                <Input
                  value={editingTemplate.description || ""}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">From name</Label>
                  <Input
                    value={editingTemplate.from_name || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, from_name: e.target.value })}
                    placeholder="SOCIÉTÉ EXEMPLE"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">From email (vide = sender SMTP)</Label>
                  <Input
                    value={editingTemplate.from_email || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, from_email: e.target.value })}
                    placeholder="amo@..."
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Reply-To</Label>
                  <Input
                    value={editingTemplate.reply_to || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, reply_to: e.target.value })}
                    placeholder="amo@..."
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Objet *</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
                <p className="text-[10px] text-zinc-500 mt-1">Variables : <code>{`{{nom_var}}`}</code></p>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Corps HTML</Label>
                <Textarea
                  value={editingTemplate.html_body}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, html_body: e.target.value })}
                  rows={10}
                  className="bg-zinc-800 border-zinc-700 font-mono text-xs"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Corps texte (fallback)</Label>
                <Textarea
                  value={editingTemplate.text_body}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, text_body: e.target.value })}
                  rows={6}
                  className="bg-zinc-800 border-zinc-700 font-mono text-xs"
                />
              </div>
              {/* Variables documentées (read-only display, edit via JSON serait + complexe) */}
              {(editingTemplate.variables || []).length > 0 && (
                <div>
                  <Label className="text-zinc-400 text-xs">Variables documentées</Label>
                  <div className="space-y-1 mt-1 max-h-32 overflow-y-auto border border-zinc-800 rounded p-2 bg-zinc-800/30">
                    {editingTemplate.variables.map((v, i) => (
                      <div key={i} className="text-[11px] flex gap-2">
                        <code className="text-purple-400">{`{{${v.key}}}`}</code>
                        {v.required && <span className="text-red-400">*</span>}
                        <span className="text-zinc-500">— {v.description || "—"}</span>
                        {v.default && <span className="text-zinc-600">(défaut: {String(v.default).slice(0, 30)})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingTemplate(null)}>Annuler</Button>
            <Button onClick={saveTemplate} className="bg-cyan-600 hover:bg-cyan-500">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG : PREVIEW ═══ */}
      <Dialog open={!!previewTemplate} onOpenChange={(o) => !o && setPreviewTemplate(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              Preview : {previewTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-3">
              <div>
                <p className="text-xs text-zinc-500 mb-2">Variables (modifiables pour tester) :</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(previewTemplate.variables || []).map((v) => (
                    <div key={v.key} className="flex items-center gap-2">
                      <code className="text-xs text-purple-400 w-32 shrink-0">{`{{${v.key}}}`}</code>
                      <Input
                        value={previewVars[v.key] || ""}
                        onChange={(e) => setPreviewVars({ ...previewVars, [v.key]: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-xs h-7 flex-1"
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={runPreview} size="sm" className="mt-2 bg-cyan-600 hover:bg-cyan-500">
                  <Send className="w-3.5 h-3.5 mr-1" /> Générer l'aperçu
                </Button>
              </div>
              {previewResult && (
                <div className="space-y-3 border-t border-zinc-800 pt-3">
                  <div>
                    <p className="text-xs text-zinc-500">Objet :</p>
                    <p className="text-sm text-white font-medium">{previewResult.subject}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 mb-1">Rendu HTML :</p>
                    <div
                      className="bg-white text-black p-4 rounded border border-zinc-700 max-h-96 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: previewResult.html }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewTemplate(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG : ÉDITEUR SENDER (basique) ═══ */}
      <Dialog open={!!editingSender} onOpenChange={(o) => !o && setEditingSender(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Modifier l'expéditeur</DialogTitle>
          </DialogHeader>
          {editingSender && (
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-400 text-xs">Nom affiché (display_name)</Label>
                <Input
                  value={editingSender.display_name}
                  onChange={(e) => setEditingSender({ ...editingSender, display_name: e.target.value })}
                  placeholder="SOCIÉTÉ EXEMPLE"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Email</Label>
                <Input
                  value={editingSender.email}
                  onChange={(e) => setEditingSender({ ...editingSender, email: e.target.value })}
                  placeholder="amo@exemple.invalid"
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Reply-To (optionnel)</Label>
                <Input
                  value={editingSender.reply_to}
                  onChange={(e) => setEditingSender({ ...editingSender, reply_to: e.target.value })}
                  placeholder="support@..."
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="sender-enabled"
                  checked={editingSender.enabled}
                  onChange={(e) => setEditingSender({ ...editingSender, enabled: e.target.checked })}
                />
                <Label htmlFor="sender-enabled" className="text-zinc-300 text-sm cursor-pointer">Sender actif</Label>
              </div>
              <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-800">
                Pour éditer la config SMTP (host, port, password, test) → Management → Configuration Email
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingSender(null)}>Annuler</Button>
            <Button onClick={saveSender} className="bg-cyan-600 hover:bg-cyan-500">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOG : ÉDITEUR DESTINATAIRES ═══ */}
      <Dialog open={!!editingRecipients} onOpenChange={(o) => !o && setEditingRecipients(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRecipients?._is_new ? "Nouveaux destinataires" : `Modifier : ${editingRecipients?.name}`}
            </DialogTitle>
          </DialogHeader>
          {editingRecipients && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Event key *</Label>
                  <Input
                    value={editingRecipients.event_key}
                    onChange={(e) => setEditingRecipients({ ...editingRecipients, event_key: e.target.value })}
                    disabled={!editingRecipients._is_new}
                    placeholder="ag04_daily_report"
                    className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Tenant</Label>
                  <Input
                    value={editingRecipients.tenant_id || ""}
                    onChange={(e) => setEditingRecipients({ ...editingRecipients, tenant_id: e.target.value })}
                    placeholder="tef"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Nom affiché</Label>
                <Input
                  value={editingRecipients.name}
                  onChange={(e) => setEditingRecipients({ ...editingRecipients, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Description</Label>
                <Input
                  value={editingRecipients.description || ""}
                  onChange={(e) => setEditingRecipients({ ...editingRecipients, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              {["to", "cc", "bcc"].map((field) => (
                <div key={field}>
                  <Label className="text-zinc-400 text-xs uppercase">{field} (1 email par ligne)</Label>
                  <Textarea
                    value={(editingRecipients[field] || []).join("\n")}
                    onChange={(e) => setEditingRecipients({
                      ...editingRecipients,
                      [field]: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean),
                    })}
                    rows={2}
                    className="bg-zinc-800 border-zinc-700 font-mono text-xs"
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingRecipients(null)}>Annuler</Button>
            <Button onClick={saveRecipients} className="bg-cyan-600 hover:bg-cyan-500">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
